import type {KeyboardEvent as ReactKeyboardEvent, PointerEvent as ReactPointerEvent} from 'react';
import {useCallback, useEffect, useRef} from 'react';

import {CALCULATOR_DOCK_INLINE_SIZE_VAR} from './calculatorWindow.store';

/** Room the host page keeps by default, so the question stays readable. */
export const DOCK_MIN_HOST_PX = 480;

/** Distance one arrow-key press moves the edge. */
const NUDGE_PX = 16;

/** The docked width in words, for the resize handle's `aria-valuetext`. */
export const dockWidthText = (px: number): string => `${Math.round(px)} pixels wide`;

interface ResizeBounds {
  min: number;
  max: number;
}

interface UseCalculatorDockResizeOptions {
  /** Committed width, or `null` to start from `defaultWidth`. */
  width: number | null;
  /** The type's Figma width. Doubles as the minimum — see the note on bounds. */
  defaultWidth: number;
  /** Called once per gesture, on release — not per frame. */
  onCommit: (width: number) => void;
  /** The window being resized. Measured to clamp, and written to during a drag. */
  windowRef: React.RefObject<HTMLElement | null>;
  /** Resizing is docked-mode only — floating and expanded windows have fixed widths. */
  enabled: boolean;
  /** Room the host page keeps beside the rail; the rail can't grow into it. */
  hostMinWidth: number;
}

/** Spread straight onto the resize handle element. */
interface UseCalculatorDockResizeReturn {
  onPointerDown: (event: ReactPointerEvent<HTMLElement>) => void;
  onPointerMove: (event: ReactPointerEvent<HTMLElement>) => void;
  onPointerUp: (event: ReactPointerEvent<HTMLElement>) => void;
  onPointerCancel: (event: ReactPointerEvent<HTMLElement>) => void;
  onKeyDown: (event: ReactKeyboardEvent<HTMLElement>) => void;
  onKeyUp: () => void;
  onBlur: () => void;
}

const clampTo = (width: number, bounds: ResizeBounds) =>
  Math.round(Math.min(Math.max(width, bounds.min), bounds.max));

/**
 * The docked width as CSS, clamped so it keeps tracking the viewport on its own.
 * Every writer of the custom property must use this: a plain pixel value would
 * pin the rail at its last width and let a later viewport shrink squeeze the
 * host page out.
 */
export const dockInlineSize = (defaultWidth: number, width: number, hostMinWidth: number) =>
  `clamp(${defaultWidth}px, ${width}px, calc(100vw - ${hostMinWidth}px))`;

/**
 * Pointer-drag plus arrow-key nudge for the docked calculator's leading edge.
 *
 * A gesture resizes the rail by writing the dock custom property straight to
 * the document element, and only commits to the store on release: the property
 * drives both the window's own width and the host page's margin, so one write
 * moves the two in lockstep, and no frame of the drag re-renders the window or
 * the calculator the student is not looking at. The graphing calculator does
 * re-render per frame regardless — its own ResizeObserver tracks the panel so
 * the plot keeps up with the drag.
 *
 * The rail can only grow. Every narrower width is a degraded layout — the
 * scientific keypad is a fixed pixel grid and starts to scroll, and the
 * graphing sidebar is already at its bottom-drawer breakpoint — and the default
 * is the width the design specifies.
 */
export function useCalculatorDockResize({
  width,
  defaultWidth,
  onCommit,
  windowRef,
  enabled,
  hostMinWidth
}: UseCalculatorDockResizeOptions): UseCalculatorDockResizeReturn {
  const gestureRef = useRef<{
    pointerX: number;
    origin: number;
    bounds: ResizeBounds;
    moved: boolean;
  } | null>(null);
  const liveRef = useRef<number>(width ?? defaultWidth);
  const keyboardDirtyRef = useRef(false);

  useEffect(() => {
    liveRef.current = width ?? defaultWidth;
  }, [width, defaultWidth]);

  const measureBounds = useCallback(
    (): ResizeBounds => ({
      min: defaultWidth,
      max: Math.max(defaultWidth, window.innerWidth - hostMinWidth)
    }),
    [defaultWidth, hostMinWidth]
  );

  /**
   * Where the rail's edge actually is, which is not always the stored width:
   * CSS clamps the rail against the viewport, so every gesture starts from what
   * the student can see rather than from a number that may be off-screen.
   */
  const renderedWidth = useCallback(
    () => windowRef.current?.getBoundingClientRect().width ?? liveRef.current,
    [windowRef]
  );

  const resizeTo = useCallback(
    (next: number) => {
      liveRef.current = next;
      document.documentElement.style.setProperty(
        CALCULATOR_DOCK_INLINE_SIZE_VAR,
        dockInlineSize(defaultWidth, next, hostMinWidth)
      );
    },
    [defaultWidth, hostMinWidth]
  );

  const endGesture = useCallback(
    (event: ReactPointerEvent<HTMLElement>) => {
      if (!gestureRef.current) {
        return;
      }
      const {moved} = gestureRef.current;
      gestureRef.current = null;
      if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }
      // A click that never moved must not commit: it would store the rendered
      // width over a wider stored one, and re-key the graphing canvas redraw.
      if (moved) {
        onCommit(liveRef.current);
      }
    },
    [onCommit]
  );

  // Leaving docked mode unmounts the handle mid-gesture, so pointerup never
  // arrives. The uncommitted width has to go with the gesture, or the next
  // arrow key would nudge from a width that was never stored.
  /* eslint-disable react-you-might-not-need-an-effect/no-event-handler -- cancels an in-flight pointer gesture the parent cannot observe */
  useEffect(() => {
    if (!enabled) {
      gestureRef.current = null;
      keyboardDirtyRef.current = false;
      liveRef.current = width ?? defaultWidth;
    }
  }, [enabled, width, defaultWidth]);
  /* eslint-enable react-you-might-not-need-an-effect/no-event-handler */

  const onPointerDown = useCallback(
    (event: ReactPointerEvent<HTMLElement>) => {
      if (!enabled || event.button !== 0) {
        return;
      }
      const origin = renderedWidth();
      // Suppresses text selection during the drag, and with it the focus the
      // handle would otherwise get — which the arrow keys depend on.
      event.preventDefault();
      event.currentTarget.focus();
      gestureRef.current = {pointerX: event.clientX, origin, bounds: measureBounds(), moved: false};
      event.currentTarget.setPointerCapture?.(event.pointerId);
    },
    [enabled, measureBounds, renderedWidth]
  );

  const onPointerMove = useCallback(
    (event: ReactPointerEvent<HTMLElement>) => {
      const gesture = gestureRef.current;
      if (!gesture || !enabled) {
        return;
      }
      gesture.moved = true;
      // The rail is anchored to the trailing edge, so dragging the handle
      // leftward widens it.
      resizeTo(clampTo(gesture.origin + gesture.pointerX - event.clientX, gesture.bounds));
    },
    [enabled, resizeTo]
  );

  const onKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLElement>) => {
      if (!enabled) {
        return;
      }
      // Arrow keys move the separator, per the WAI-ARIA window-splitter
      // pattern: leftward is a wider rail.
      const delta = {ArrowLeft: NUDGE_PX, ArrowRight: -NUDGE_PX}[event.key];
      if (!delta) {
        return;
      }
      event.preventDefault();
      const next = clampTo(renderedWidth() + delta, measureBounds());
      resizeTo(next);
      // Held keys skip the commit below, so the handle's value is updated in place.
      event.currentTarget.setAttribute('aria-valuenow', String(next));
      event.currentTarget.setAttribute('aria-valuetext', dockWidthText(next));
      // Auto-repeat fires this every ~30ms; committing each one would re-render
      // the calculator body — and redraw the graphing canvas — per frame.
      if (event.repeat) {
        keyboardDirtyRef.current = true;
      } else {
        onCommit(next);
      }
    },
    [enabled, measureBounds, onCommit, renderedWidth, resizeTo]
  );

  // Also on blur: losing focus mid-hold means keyup never arrives, stranding a
  // width the student can see but the store has not recorded.
  const onKeyUp = useCallback(() => {
    if (!keyboardDirtyRef.current) {
      return;
    }
    keyboardDirtyRef.current = false;
    onCommit(liveRef.current);
  }, [onCommit]);

  return {
    onPointerDown,
    onPointerMove,
    onPointerUp: endGesture,
    onPointerCancel: endGesture,
    onKeyDown,
    onKeyUp,
    onBlur: onKeyUp
  };
}
